import React, { useEffect, useRef, useState } from "react";
import TruncateInside from "react-truncate-inside";

export interface DynamicTruncateInsideProps {
  text?: string;
  ellipsis: string;
}

const DynamicTruncateInside: React.FC<DynamicTruncateInsideProps> = ({
  text,
  ellipsis,
}) => {
  const paddingRight = 4;
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState<number>(
    text ? Math.floor(text.length / 2) : 0,
  );
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  // Hide the text until truncation settles: react-truncate-inside re-truncates
  // over several frames after mount, which otherwise looks like the text
  // churning on load. Revealed once the rendered text stops changing (below).
  const [ready, setReady] = useState(false);

  // Reset visibility whenever the text changes so a new value re-settles before showing.
  useEffect(() => {
    setReady(false);
  }, [text]);

  useEffect(() => {
    // Function to calculate an offset that puts the ellipsis approximately in the middle
    const calculateOffset = () => {
      if (!containerRef.current) return;

      const availableWidth = containerRef.current.clientWidth + paddingRight;

      if (availableWidth === containerWidth) return;

      setContainerWidth(availableWidth);

      if (!text) return;

      // Use requestAnimationFrame to avoid synchronous layout calculations
      // which can cause the ResizeObserver loop error
      requestAnimationFrame(() => {
        if (!containerRef.current) return;
        try {
          // Create a temporary span to measure text width
          const tempSpan = document.createElement("span");
          tempSpan.style.visibility = "hidden";
          tempSpan.style.position = "absolute";
          tempSpan.style.whiteSpace = "nowrap";
          tempSpan.style.font = window.getComputedStyle(
            containerRef.current,
          ).font;
          document.body.appendChild(tempSpan);

          tempSpan.textContent = ellipsis;
          const ellipsisWidth = tempSpan.getBoundingClientRect().width;
          tempSpan.textContent = text;
          const fullTextWidth = tempSpan.getBoundingClientRect().width;
          const needsTruncation = fullTextWidth > availableWidth;

          if (!needsTruncation) {
            document.body.removeChild(tempSpan);
            return;
          }

          const avgCharWidth = fullTextWidth / text.length;
          const availableChars =
            (availableWidth - ellipsisWidth) / avgCharWidth;
          const charsPerSide = availableChars / 2;
          const calculatedOffset =
            Math.min(Math.round(charsPerSide), Math.round(text.length / 2)) - 1;

          document.body.removeChild(tempSpan);
          setOffset(calculatedOffset);
        } catch (e) {
          console.warn("Error calculating truncate offset:", e);
        }
      });
    };

    calculateOffset();

    let resizeTimeout: number | null = null;

    // Debounce resize events to avoid too frequent recalculations
    if (containerRef.current && window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        if (resizeTimeout) {
          window.cancelAnimationFrame(resizeTimeout);
        }
        resizeTimeout = window.requestAnimationFrame(calculateOffset);
      });

      resizeObserver.observe(containerRef.current);
      return () => {
        resizeObserver.disconnect();
        if (resizeTimeout) {
          window.cancelAnimationFrame(resizeTimeout);
        }
      };
    }
  }, [containerWidth, ellipsis, text]);

  // Reveal once settled: poll the rendered text each frame (textContent reads
  // even while visibility:hidden) and show it once it's unchanged across frames.
  // A timeout fail-safe ensures it's never left permanently hidden.
  useEffect(() => {
    if (ready || containerWidth === null) return;
    if (!text) {
      setReady(true);
      return;
    }

    let raf = 0;
    let previous: string | null = null;
    let stableFrames = 0;
    const start = performance.now();

    const check = () => {
      const current = contentRef.current?.textContent ?? "";
      if (current !== "" && current === previous) {
        stableFrames += 1;
        if (stableFrames >= 2) {
          setReady(true);
          return;
        }
      } else {
        stableFrames = 0;
      }
      previous = current;

      if (performance.now() - start < 1000) {
        raf = requestAnimationFrame(check);
      } else {
        setReady(true); // fail-safe
      }
    };

    raf = requestAnimationFrame(check);
    return () => cancelAnimationFrame(raf);
  }, [ready, containerWidth, text, offset]);

  return (
    <div ref={containerRef} style={{ paddingRight: `${paddingRight}px` }}>
      {containerWidth !== null && (
        // Hidden but still laid out (width measurement unaffected) until settled.
        <div
          ref={contentRef}
          style={{ visibility: ready ? "visible" : "hidden" }}
        >
          <TruncateInside
            text={text || ""}
            ellipsis={ellipsis}
            offset={offset}
            width={containerWidth - paddingRight}
          />
        </div>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(DynamicTruncateInside);
